import ParisFactory from '@baidu/paris';
import setupOfflineToken from '@baidu/paris/lib/offlineToken';
import type {RawAxiosRequestHeaders} from 'axios';
import type {ParisInstance, TParisFactory} from '@baidu/paris';
import type {BeforeRequestHook} from '@/services/request/types';

const PARIS_SID = Number(import.meta.env.VITE_PARIS_SID);
const PARIS_SAK = import.meta.env.VITE_PARIS_SAK;
const ACS_TOKEN_TIMEOUT = 8000;
const ACS_TOKEN_TIMEOUT_ERROR_CODE = '601';

const BANTI_URL = `https://banti-static.cdn.bcebos.com/o/static/banti_${PARIS_SAK}.js`;
const ACS_URL = `https://dlswbr.baidu.com/heicha/mm/${PARIS_SID}/acs-${PARIS_SID}.js`;
const ACS_DISASTER_URL = `https://miaowu.baidu.com/sdk/heicha/mm/${PARIS_SID}/acs-${PARIS_SID}.js`;

type ParisFactoryWithOfflineToken = TParisFactory & {
    createOfflineToken?: (sidOrSak: string | number, errorCode: string | number) => string;
};

type RequestIdleCallback = (callback: () => void) => number;
type WindowWithRequestIdleCallback = Window & {
    requestIdleCallback?: RequestIdleCallback;
};
interface PerformanceObserverEntryList {
    getEntries: () => PerformanceEntry[];
}
interface PerformanceObserverConstructor {
    supportedEntryTypes?: readonly string[];
    new (callback: (list: PerformanceObserverEntryList) => void): PerformanceObserver;
}
type WindowWithPerformanceObserver = Window & {
    PerformanceObserver?: PerformanceObserverConstructor;
};

interface HeaderCarrier {
    headers?: RawAxiosRequestHeaders | Headers | null;
}

let parisInstance: ParisInstance | null = null;
let offlineTokenSetupDone = false;
let antiCheatInitScheduled = false;

/**
 * BANTI 初始化自动上报使用的页面级 subid。
 *
 * 由业务方自主决定，目前按页面场景分。受保护接口加签时也复用这个页面级 subid，
 * 和文档“根据当前页面返回页面英文名”的建议保持一致。
 */
function getPageSubid(): string {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';

    if (path.startsWith('/project/episode')) return 'episode';
    if (path.startsWith('/canvas')) return 'canvas';
    if (path.startsWith('/script')) return 'script';
    if (path.startsWith('/asset') || path.startsWith('/workspace/assets')) return 'assets';
    if (path.startsWith('/project') || path.startsWith('/workspace')) return 'project';
    return 'home';
}

/**
 * 初始化 Paris，自动加载 BANTI 风控 JSSDK 和 ACS 接口签名 JSSDK。
 *
 * 按文档要求尽量在页面加载早期调用，保证 BANTI 采集数据完整。
 * setupOfflineToken 只注册一次，避免 React StrictMode 或模块复用时重复初始化。
 */
export function initAntiCheat(): ParisInstance | null {
    if (typeof window === 'undefined') return null;
    if (parisInstance) return parisInstance;

    if (!offlineTokenSetupDone) {
        setupOfflineToken(ParisFactory);
        offlineTokenSetupDone = true;
    }

    parisInstance = ParisFactory.create({
        sid: PARIS_SID,
        sak: PARIS_SAK,
        timeout: 5000,
        acsTimeout: 10000,
        bantiTimeout: 10000,
        bantiUrl: BANTI_URL,
        acsUrl: ACS_URL,
        bantiOptions: {
            subid: getPageSubid,
        },
        disasterConfig: {
            acsUrl: ACS_DISASTER_URL,
        },
    });

    return parisInstance;
}

function hasFcp(): boolean {
    return performance
        .getEntriesByType('paint')
        .some((entry) => entry.name === 'first-contentful-paint');
}

function runAfterFcp(callback: () => void): void {
    if (hasFcp()) {
        callback();
        return;
    }

    const PerformanceObserverCtor = (window as WindowWithPerformanceObserver).PerformanceObserver;
    const supportedEntryTypes = PerformanceObserverCtor?.supportedEntryTypes;
    if (typeof PerformanceObserverCtor !== 'function' || !supportedEntryTypes?.includes('paint')) {
        window.setTimeout(callback, 0);
        return;
    }

    const observer = new PerformanceObserverCtor((list) => {
        if (!list.getEntries().some((entry) => entry.name === 'first-contentful-paint')) {
            return;
        }
        observer.disconnect();
        callback();
    });

    try {
        observer.observe({type: 'paint', buffered: true});
    } catch {
        window.setTimeout(callback, 0);
    }
}

function runWhenIdle(callback: () => void): void {
    const requestIdleCallback = (window as WindowWithRequestIdleCallback).requestIdleCallback;

    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(callback);
        return;
    }

    window.setTimeout(callback, 0);
}

/**
 * 在 FCP 之后等待浏览器主线程空闲时初始化 Paris，避免 BANTI 脚本抢占首屏关键阶段。
 *
 * 如果某个受保护请求先发起，appendAcsTokenHeader 仍会通过 initAntiCheat 立即兜底。
 */
export function initAntiCheatWhenIdle(): void {
    if (typeof window === 'undefined') return;
    if (antiCheatInitScheduled || parisInstance) return;
    antiCheatInitScheduled = true;

    const init = () => {
        antiCheatInitScheduled = false;
        initAntiCheat();
    };
    runAfterFcp(() => runWhenIdle(init));
}

/**
 * 生成离线 Acs-Token。
 *
 * Paris 开启 offlineToken 后，如果 ACS-TOKEN 生成异常或超时，用 OFFPT_ 前缀的
 * 离线 token 兜底，避免因为 SDK 异常导致业务请求被永久阻塞。
 */
function createOfflineAcsToken(errorCode: string): string {
    return (
        (ParisFactory as ParisFactoryWithOfflineToken).createOfflineToken?.(PARIS_SID, errorCode) ??
        ''
    );
}

/**
 * Paris 的 getAcsTokenWithBantiSend 回调只有一个参数，可能是正常 Acs-Token，
 * 也可能是错误码。数字错误码转换成离线 token 后再透传给后端。
 */
function normalizeAcsToken(acsTokenOrErrorCode: unknown): string {
    const value = String(acsTokenOrErrorCode ?? '');
    if (!value) return createOfflineAcsToken(ACS_TOKEN_TIMEOUT_ERROR_CODE);
    if (/^\d+$/.test(value)) return createOfflineAcsToken(value);
    return value;
}

/**
 * 每次受保护接口发起前都重新生成一次 Acs-Token。
 *
 * getAcsTokenWithBantiSend 会在生成签名的同时触发一次 BANTI 手动上报，
 * 提高关键请求的 ab_sr 覆盖率。外层 8 秒超时用于防止 SDK 内部异常阻塞业务请求。
 */
async function getAcsToken(subid: string): Promise<string> {
    const instance = initAntiCheat();
    if (!instance) return '';

    return new Promise((resolve) => {
        let resolved = false;
        let timer = 0;
        const finish = (token: unknown) => {
            if (resolved) return;
            resolved = true;
            if (timer > 0) {
                window.clearTimeout(timer);
            }
            resolve(normalizeAcsToken(token));
        };
        timer = window.setTimeout(() => {
            finish(createOfflineAcsToken(ACS_TOKEN_TIMEOUT_ERROR_CODE));
        }, ACS_TOKEN_TIMEOUT);

        instance.getAcsTokenWithBantiSend(
            {
                subid,
                isWait: true,
            },
            finish,
        );
    });
}

/**
 * 兼容 axios 普通对象 headers 和 AxiosHeaders 两种形态，统一写入 Acs-Token。
 */
function setAcsTokenHeader<T extends HeaderCarrier>(config: T, acsToken: string): T {
    if (!acsToken) return config;

    const headers = config.headers;
    if (headers && typeof (headers as {set?: unknown}).set === 'function') {
        (headers as {set: (name: string, value: string) => void}).set('Acs-Token', acsToken);
        return config;
    }

    config.headers = {
        ...((headers ?? {}) as RawAxiosRequestHeaders),
        'Acs-Token': acsToken,
    };
    return config;
}

/**
 * 给外部请求配置追加 Acs-Token。
 *
 * 供非统一 request 实例的请求链路复用，例如注入式 Agent SDK 的 requestInterceptor。
 */
export async function appendAcsTokenHeader<T extends HeaderCarrier>(config: T): Promise<T> {
    const acsToken = await getAcsToken(getPageSubid());
    return setAcsTokenHeader(config, acsToken);
}

/**
 * 统一请求层的 beforeRequest hook。
 *
 * 由单个请求通过 acsToken 显式声明是否生成并追加 Acs-Token。
 */
export const antiCheatBeforeRequest: BeforeRequestHook = async (config) => {
    const shouldSign = config.acsToken === true;
    delete config.acsToken;
    if (!shouldSign) return;

    await appendAcsTokenHeader(config);
};
